import {ResearchLitElement, html, css} from '@core/research-lit-element';
import '@bog-design-system/bd-list-item';
import '@bog-design-system/bd-checkbox';
import '@bog-design-system/bd-radio-group';
import '@bog-design-system/bd-notification-badge';
import {BdIconButton} from '@bog-design-system/bd-icon-button';

/**
 * `research-filter-item`
 * filter item
 *
 * @customElement
 * @lit-element
 * @design https://www.figma.com/file/IIjl4hyfYtsYzuatJGf5cX/Filter-Component?node-id=1%3A216&t=N0VZTXcaxbiL1Bwt-0
 */
export class ResearchFilterItem extends ResearchLitElement {
    static get is() {
        return 'research-filter-item';
    }

    static get styles() {
        // language=CSS
        return css`
            :host {
                display: block;
                width: 100%;
                --item-bg-color: var(--color-invert-component-tr-90);
                --label-color: var(--color-invert-component-tr-20);
                ---li-bg-active: var(--color-invert-component-tr-80);
                ---li-bg-hover: var(--color-invert-component-tr-80);
                --item-min-height: var(--space-32);
                --li-padding-left: var(--space-16);
                --li-padding-right: var(--space-16);
                --item-hover-color: var(--color-invert-component-tr-70);
            }

            .filter-item {
                width: 100%;
                color: var(--label-color);
                --li-bg: var(--item-bg-color);
                --li-min-height: var(--item-min-height);
                --padding-left: var(--li-padding-left);
                --padding-right: var(--li-padding-right);
                --li-bg-hover: var(---li-bg-hover);
                --li-bg-active: var(---li-bg-active);
            }

            .badge {
                --badge-bg: var(--color-invert-component-tr-70);
                --badge-color: var(--color-invert-component-tr-40);
                min-width: 24px;
            }

            .filter-item:hover {
                --icon-bg-color: var(--item-hover-color);
            }

            .icon-btn {
                margin-left: var(--space-4);
                background-color: var(--icon-bg-color);
            }

            .sub-categories-wrapper {
                display: none;
                width: 100%;
                height: 0px;
                transition: height 0.5s, display 0.5s linear;
            }

            :host([show-subFilters][active]) .sub-categories-wrapper {
                display: block;
                height: auto;
            }

            .sub-categories-wrapper ::slotted(research-filter-item) {
                --li-padding-left: 46px;
            }

            bd-radio-button {
                align-items: center;
            }
        `;
    }

    render() {
        // language=HTML
        return html`
            <bd-list-item class="filter-item" @click="${(e) => this._filterItemClick(e)}" no-border>
                ${this.type === this.validTypes[1] ?
                    html`
                          <bd-radio-button
                              slot="center"
                              ?checked="${this.checked}"
                              value="${this.value}"
                          ></bd-radio-button>
                      ` :
                    html`
                          <bd-checkbox
                              slot="center"
                              ?checked="${this.checked || this._allItemsChcecked}"
                              ?active="${this.showSubFilters && !this._allItemsChcecked && this._itemChcecked}"
                              value="${this.value}"
                          ></bd-checkbox>
                      `}

                <bd-font type="button-text" slot="center"> ${this.label} </bd-font>

                ${!this.hideCount ?
                    html`
                          <bd-notification-badge
                              slot="right"
                              size="small"
                              class="badge"
                              max-value="${this.maxCount}"
                              amount="${this.itemsCount}"
                          >
                          </bd-notification-badge>
                      ` :
                    ''}
                ${this.showSubFilters ?
                    html`
                          <bd-icon-button
                              type="secondary-grey"
                              size="small"
                              slot="right"
                              class="icon-btn"
                              icon="${this.active ? 'bd:chevron_up' : 'bd:chevron_down'}"
                              @click="${this._activeChanged}"
                          >
                          </bd-icon-button>
                      ` :
                    ''}
            </bd-list-item>

            <div class="sub-categories-wrapper">
                <slot name="sub-filters" id="subFilters" @slotchange="${this._onSubFiltersChanged}"></slot>
            </div>
        `;
    }

    static get properties() {
        return {
            /**
             * @property {string} title
             * filter title
             */
            label: {
                type: String,
            },
            /**
             * @property {string} value
             * filter item value
             */
            value: {
                type: String,
            },
            /**
             * @property {boolean} checked
             * checked property defines if item is checked
             */
            checked: {
                type: Boolean,
                reflect: true,
            },
            /**
             * @property {array} subFilters
             * used to display subFilters
             */
            subFilters: {
                type: Array,
            },
            /**
             * @property {string} type
             * multiple or single selector
             */
            type: {
                type: String,
                reflect: true,
                attribute: 'type',
            },
            /**
             * @property {string} subFiltertype
             * multiple or single selector for sub filter
             */
            subFiltertype: {
                type: String,
                reflect: true,
                attribute: 'sub-filter-type',
            },
            /**
             * @property {boolean} active
             * active property defines filter-container is opened or closed
             */
            active: {
                type: Boolean,
                reflect: true,
            },
            /**
             * @property {number} itemsCount
             * itemsCount property defines notification badge count
             */
            itemsCount: {
                type: Number,
                reflect: true,
                attribute: 'items-count',
            },
            /**
             * @property {number} maxCount
             * maxCount property defines notification badge max-count
             */
            maxCount: {
                type: Number,
                reflect: true,
                attribute: 'max-value',
            },
            /**
             * @property {boolean} hideCount
             * hideCount property defines if notification badge is displayed
             */
            hideCount: {
                type: Boolean,
                reflect: true,
                attribute: 'hide-count',
            },
            showSubFilters: {
                type: Boolean,
                reflect: true,
                attribute: 'show-subFilters',
            },
            /**
             * @property {boolean} _allItemsChcecked
             * _allItemsChcecked property defines if all subItems are checked
             */
            _allItemsChcecked: {
                type: Boolean,
            },
            /**
             * @property {boolean} _itemChcecked
             * _itemChcecked property defines if at least one item is checked
             */
            _itemChcecked: {
                type: Boolean,
            },
        };
    }

    constructor() {
        super();
        this.active = false;
        this.maxCount = 99;
        this.hideCount = false;
        this.type = this.validTypes[0];
        this.subFiltertype = this.validTypes[0];
        this._allItemsChcecked = false;
        this._itemChcecked = false;
        this.showSubFilters = false;
    }

    _filterItemClick(e) {
        if (!(e.target instanceof BdIconButton)) {
            const oldVal = this.checked;
            if (this.type === this.validTypes[0]) {
                this.checked = !this.checked;
            } else {
                this.checked = true;
            }
            if (oldVal !== this.checked) {
                this.dispatchEvent(
                    new CustomEvent('filter-check-changed', {
                        detail: {
                            value: this.value,
                            checked: this.checked,
                        },
                        bubbles: true,
                    })
                );
            }

            if (this.showSubFilters && this.type == this.validTypes[0]) {
                this._countCheckedSubItems();
            }
        }
    }

    _onSubFiltersChanged(event) {
        const nodes = event.target.assignedNodes();
        if (nodes.length > 0) {
            this.showSubFilters = true;
            for (const node of nodes) {
                if (node instanceof ResearchFilterItem) {
                    node.type = this.subFiltertype;
                    if (node.checked) {
                        this.checked = true;
                    }
                    node.addEventListener('click', this._selectSubFilter.bind(this));
                }
            }
        } else {
            this.showSubFilters = false;
        }
    }

    _selectSubFilter(e) {
        if (this.subFiltertype === this.validTypes[1]) {
            const subFilterItems = this.shadowRoot.querySelector('#subFilters').assignedNodes();
            for (const item of subFilterItems) {
                if (item instanceof ResearchFilterItem) {
                    if (item !== e.target) {
                        item.checked = false;
                    }
                }
            }
        }
        this.dispatchEvent(
            new CustomEvent('sub-filter-check-changed', {
                detail: {
                    value: e.target.value,
                    checked: e.target.checked,
                },
                bubbles: true,
            })
        );

        this._countCheckedSubItems();
    }

    _activeChanged() {
        this.active = !this.active;
    }

    get validTypes() {
        return ['multiple', 'single'];
    }

    _uncheckeSubItems() {
        const subFilterItems = this.shadowRoot.querySelector('#subFilters').assignedNodes();
        for (const item of subFilterItems) {
            if (item instanceof ResearchFilterItem) {
                item.checked = false;
            }
        }
    }

    _checkAllSubItems() {
        const subFilterItems = this.shadowRoot.querySelector('#subFilters').assignedNodes();
        for (const item of subFilterItems) {
            if (item instanceof ResearchFilterItem) {
                item.checked = true;
            }
        }
    }

    _countCheckedSubItems() {
        let count = 0;
        const subFilterItems = this.shadowRoot.querySelector('#subFilters').assignedNodes();
        const totalCount = subFilterItems.length;

        for (const item of subFilterItems) {
            if (item instanceof ResearchFilterItem && item.checked) {
                count++;
            }
        }
        this._allItemsChcecked = totalCount === count && count > 0;
        this._itemChcecked = count > 0;

        if (this.type === this.validTypes[0]) {
            if (this._allItemsChcecked) {
                this.checked = true;
            } else {
                this.checked = false;
            }
        }
    }
}
window.customElements.define(ResearchFilterItem.is, ResearchFilterItem);
